import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  GoalAssigneeType,
  GoalMetric,
  GoalPeriod,
  GoalQuery,
  GoalResponse,
} from '@troia-v3/types';

import { Button, DeleteConfirmationModal, FiltersButton, FilterBottomSheet, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, PlainHeadCell, SelectAllHeadCell, SortableHeadCell, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useGoals, useDeleteGoal } from '../hooks/useGoals';
import { GoalFormDrawer } from '../components/GoalFormDrawer';
import { GoalCard } from '../components/GoalCard';
import { GoalListItem } from '../components/GoalListItem';
import {
  ASSIGNEE_TYPE_LABEL,
  METRIC_LABEL,
  PERIOD_LABEL,
} from '../utils/goalDisplay';

type ViewMode = 'grid' | 'table';
type SortKey = 'assignee' | 'metric' | 'period' | 'target';

const VIEW_MODE_STORAGE_KEY = 'goals_view_mode';

const ASSIGNEE_FILTER_OPTIONS: ReadonlyArray<[GoalAssigneeType | undefined, string]> = [
  [undefined, 'Todas'],
  ['user', 'Vendedor'],
  ['team', 'Equipe'],
  ['company', 'Empresa'],
];

const METRIC_FILTER_OPTIONS: ReadonlyArray<[GoalMetric | undefined, string]> = [
  [undefined, 'Todas'],
  ['revenue', 'Receita'],
  ['deals', 'Negócios fechados'],
  ['calls', 'Ligações realizadas'],
  ['meetings', 'Reuniões realizadas'],
  ['qualifiedLeads', 'Leads qualificados'],
  ['proposalsSent', 'Propostas enviadas'],
];

const PERIOD_FILTER_OPTIONS: ReadonlyArray<[GoalPeriod | undefined, string]> = [
  [undefined, 'Todos'],
  ['month', 'Mensal'],
  ['quarter', 'Trimestral'],
  ['year', 'Anual'],
];

const PERIOD_ORDER: Record<GoalPeriod, number> = { month: 0, quarter: 1, year: 2 };

export const GoalsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState<GoalQuery>({ page: 1, limit: 20 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'assignee', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<GoalResponse | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      return stored === 'grid' || stored === 'table' ? stored : 'table';
    } catch {
      return 'table';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<GoalResponse | null>(null);
  const [duplicating, setDuplicating] = useState<GoalResponse | null>(null);

  const { data, isLoading, error } = useGoals(query);
  const deleteGoal = useDeleteGoal();

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (backend não expõe sort para metas).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'assignee') {
        cmp = (a.assigneeName ?? '').localeCompare(b.assigneeName ?? '', 'pt-BR');
      } else if (sort.key === 'metric') {
        cmp = METRIC_LABEL[a.metric].localeCompare(METRIC_LABEL[b.metric], 'pt-BR');
      } else if (sort.key === 'period') {
        cmp = PERIOD_ORDER[a.period] - PERIOD_ORDER[b.period];
      } else {
        cmp = a.targetValue - b.targetValue;
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((g) => selectedIds.has(g.id));
  const someOnPageSelected = items.some((g) => selectedIds.has(g.id));

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const setAssigneeFilter = (assigneeType?: GoalAssigneeType) => {
    setQuery((prev) => ({ ...prev, assigneeType, page: 1 }));
  };
  const setMetricFilter = (metric?: GoalMetric) => {
    setQuery((prev) => ({ ...prev, metric, page: 1 }));
  };
  const setPeriodFilter = (period?: GoalPeriod) => {
    setQuery((prev) => ({ ...prev, period, page: 1 }));
  };
  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, assigneeType: undefined, metric: undefined, period: undefined, page: 1 }));
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((g) => next.delete(g.id));
      else items.forEach((g) => next.add(g.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleNew = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  // "+" central do bottom-nav mobile → nova meta (mockup .m-fab).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); handleNew(); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, []);

  const navigate = useNavigate();

  const handleEdit = (goal: GoalResponse) => {
    setEditing(goal);
    setDrawerOpen(true);
  };

  // 'Ver progresso' — sem detalhe por-meta hoje; leva ao dashboard comercial (visão consolidada).
  const handleViewProgress = () => navigate('/dashboard/management/commercial');

  // 'Duplicar' — abre o drawer em modo cópia (pré-preenchido, próximo período sugerido).
  const handleDuplicate = (goal: GoalResponse) => setDuplicating(goal);

  const handleDelete = (goal: GoalResponse) => setDeleteTarget(goal);

  const confirmDelete = () => {
    if (deleteTarget) deleteGoal.mutate(deleteTarget.id);
    setDeleteTarget(null);
  };

  const bulkDelete = () => setBulkDeleteOpen(true);

  const confirmBulkDelete = () => {
    Array.from(selectedIds).forEach((id) => deleteGoal.mutate(id));
    clearSelection();
    setBulkDeleteOpen(false);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
  };

  const assigneeLabel = query.assigneeType ? ASSIGNEE_TYPE_LABEL[query.assigneeType] : null;
  const metricLabel = query.metric ? METRIC_LABEL[query.metric] : null;
  const periodLabel = query.period ? (PERIOD_LABEL[query.period] ?? query.period) : null;
  const activeFilterCount = [assigneeLabel, metricLabel, periodLabel].filter(Boolean).length;

  // ── Filtros popover (tema claro fixo #ffffff/#e5e7eb) ──
  const FilterSection = <T,>({
    title,
    options,
    current,
    onPick,
  }: {
    title: string;
    options: ReadonlyArray<[T | undefined, string]>;
    current: T | undefined;
    onPick: (value?: T) => void;
  }) => (
    <>
      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>
        {title}
      </p>
      {options.map(([val, label]) => {
        const active = current === val;
        return (
          <button
            key={label}
            onClick={() => { onPick(val); setFiltersOpen(false); }}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <span className="truncate">{label}</span>
            {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      <FilterSection title="Atribuição" options={ASSIGNEE_FILTER_OPTIONS} current={query.assigneeType} onPick={setAssigneeFilter} />
      <FilterSection title="Métrica" options={METRIC_FILTER_OPTIONS} current={query.metric} onPick={setMetricFilter} />
      <FilterSection title="Período" options={PERIOD_FILTER_OPTIONS} current={query.period} onPick={setPeriodFilter} />
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={<>
          {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>
              Configurações
            </span>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Metas</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
              data-testid="goals-count"
            >
              {data.total}
            </span>
          )}
        </>}
        center={
          /* Centro: busca (F17-22: ToolbarSearch shared, sempre centralizada) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar meta…"
            data-testid="goals-search"
          />
        }
        right={<>
          {/* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */}
          {/* Filtros (F05: FiltersButton shared) */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="goals-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="goals-filters-popover"
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: ViewToggle shared — Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="goals-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Nova meta (único primário — F01-03: Button shared com colapso) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={handleNew}
            data-testid="new-goal"
          >
            Nova meta
          </Button>
        </>}
        mobile={
          /* `.m-sub` do mockup: busca sunken + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearch}
              placeholder="Buscar meta…"
              data-testid="goals-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="goals-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {assigneeLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Atribuição: {assigneeLabel}
              <button onClick={() => setAssigneeFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de atribuição">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {metricLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Métrica: {metricLabel}
              <button onClick={() => setMetricFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de métrica">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {periodLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Período: {periodLabel}
              <button onClick={() => setPeriodFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de período">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="goals-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionada(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="goals-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="goals-bulk-cancel">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }} data-testid="goals-error">
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar metas: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando metas..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="target"
              message="Nenhuma meta encontrada"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie sua primeira meta para acompanhar o desempenho da equipe.'}
              actionLabel="Nova meta"
              onAction={handleNew}
            />
          )}

          {/* Grid (Cards) — F34: cards SEM checkbox, seleção em massa só na tabela */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))] gap-4" data-testid="goals-grid">
              {sortedItems.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onEdit={handleEdit}
                  onDelete={handleDelete} onViewProgress={handleViewProgress} onDuplicate={handleDuplicate}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    onEdit={handleEdit}
                    onDelete={handleDelete} onViewProgress={handleViewProgress} onDuplicate={handleDuplicate}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse" data-testid="goals-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onToggle={toggleSelectAllOnPage}
                        ariaLabel="Selecionar todas"
                        data-testid="goals-check-all"
                      />
                      <SortableHeadCell<SortKey> label="Atribuição" sortKey="assignee" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Métrica" sortKey="metric" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Período" sortKey="period" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Janela" />
                      <SortableHeadCell<SortKey> label="Meta" sortKey="target" activeKey={sort.key} dir={sort.dir} onSort={handleSort} align="right" />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((goal) => (
                      <GoalListItem
                        key={goal.id}
                        goal={goal}
                        selected={selectedIds.has(goal.id)}
                        onToggleSelect={toggleSelect}
                        onEdit={handleEdit}
                        onDelete={handleDelete} onViewProgress={handleViewProgress} onDuplicate={handleDuplicate}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              totalItems={data.total}
              pageSize={data.limit}
              itemLabel="metas"
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="goals-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      <GoalFormDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        goal={editing}
      />

      {/* Duplicar: cria uma nova meta pré-preenchida a partir de uma existente,
          sugerindo o próximo período. O usuário pode trocar responsável/período. */}
      <GoalFormDrawer
        isOpen={duplicating !== null}
        onClose={() => setDuplicating(null)}
        duplicateFrom={duplicating}
      />

      {/* Confirmação de exclusão (single) */}
      <DeleteConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        message="Tem certeza que deseja excluir esta meta?"
        isLoading={deleteGoal.isPending}
      />

      {/* Confirmação de exclusão (bulk) */}
      <DeleteConfirmationModal
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        message={`Excluir ${selectedCount} meta(s) selecionada(s)?`}
        isLoading={deleteGoal.isPending}
      />
    </div>
  );
};

GoalsPage.displayName = 'GoalsPage';
